import type { JobCategory } from '@prisma/client';
import { BusinessCategory, CustomerReminderTiming, Prisma } from '@prisma/client';

export type JobCategorySettings = Pick<
	JobCategory,
	| 'customerReminderTiming'
	| 'depositPercent'
	| 'hiddenJobNavItemIds'
	| 'includeTaxOnLaborLineItems'
	| 'includeTaxOnOtherLineItems'
	| 'isProcessingFeeOnByDefault'
	| 'laborMarkup'
	| 'overrideTaxPercent'
	| 'sendScheduledJobEmailRemindersToCustomer'
	| 'sendScheduledJobSmsRemindersToCustomer'
	| 'separateTaxesAndFees'
	| 'showAdditionalWonJobDetails'
	| 'supplierInventoryMarkup'
	| 'technicianHourlyLaborRate'
	| 'useTaxRateLookup'
>;

type JobCategoryIdentity = Pick<JobCategory, 'deletedAt' | 'hiddenAt' | 'id' | 'isDefault' | 'legacyCategory' | 'name'>;

export type JobCategoryForSettings = JobCategorySettings & JobCategoryIdentity;

/**
 * Renders a legacy enum value as the JobCategory name it was seeded under.
 *
 * Lives here rather than beside the built-in list because this module is imported by
 * client components, so it must not reach into `~/server`.
 */
export const legacyCategoryToJobCategoryName = (legacyCategory: BusinessCategory | string): string =>
	legacyCategory
		.split('_')
		.map((word) => `${word.charAt(0)}${word.slice(1).toLowerCase()}`)
		.join(' ');

// Built fresh per call. A shared object would hand every caller the same
// hiddenJobNavItemIds array, and on the server module state outlives a request.
const buildEmptySettings = (): JobCategorySettings => ({
	customerReminderTiming: CustomerReminderTiming.NIGHT_BEFORE_6PM,
	depositPercent: new Prisma.Decimal(0),
	hiddenJobNavItemIds: [],
	includeTaxOnLaborLineItems: false,
	includeTaxOnOtherLineItems: false,
	isProcessingFeeOnByDefault: false,
	laborMarkup: new Prisma.Decimal(0),
	overrideTaxPercent: new Prisma.Decimal(0),
	sendScheduledJobEmailRemindersToCustomer: false,
	sendScheduledJobSmsRemindersToCustomer: false,
	separateTaxesAndFees: false,
	showAdditionalWonJobDetails: false,
	supplierInventoryMarkup: new Prisma.Decimal(0),
	technicianHourlyLaborRate: new Prisma.Decimal(0),
	useTaxRateLookup: true,
});

/**
 * The legacy enum value a job effectively belongs to, or null for a custom category.
 *
 * When the pointer is set it is the sole authority — the enum column then holds an inert
 * RESIDENTIAL placeholder for custom categories and must not be consulted. Only a job
 * written before the pointer existed (jobCategoryId null) still speaks through its enum
 * column. Every legacy-keyed read (automations, enum-keyed settings rows, stage matching)
 * goes through this so the placeholder can never leak.
 */
export const isBusinessCategoryValue = (value: string): value is BusinessCategory => value in BusinessCategory;

/**
 * The wire key a category picker submits: the row id when one exists, the legacy enum
 * value on the no-rows fallback. Numeric keys become `jobCategoryId`, enum keys stay
 * `businessCategory`.
 */
export const categoryOptionKey = (option: { id: number | null; legacyCategory: string | null }): string => String(option.id ?? option.legacyCategory);

export function effectiveLegacyCategory({
	businessCategory,
	jobCategory,
	jobCategoryId,
}: {
	businessCategory: BusinessCategory | null;
	jobCategory: { legacyCategory: string | null } | null | undefined;
	jobCategoryId: number | null;
}): BusinessCategory | null {
	if (jobCategoryId === null) {
		return businessCategory;
	}

	const legacy = jobCategory?.legacyCategory;

	return legacy != null && isBusinessCategoryValue(legacy) ? legacy : null;
}

type CategoryScopedRow = { businessCategory: string; jobCategoryId?: number | null };

/**
 * Picks the enum-keyed settings row (QuoteSettings, QuoteReminderSettings) a job should
 * read, custom categories included. Chain: the row the job's pointer names (built-ins are
 * dual-written), then the job's effective legacy enum, then the business's default
 * category's enum — a custom category has no row of its own, so it borrows the default
 * category's content — then RESIDENTIAL.
 */
export function resolveCategoryScopedRow<T extends CategoryScopedRow>({
	businessCategory,
	jobCategories,
	jobCategoryId,
	rows,
}: {
	businessCategory: BusinessCategory | string | null | undefined;
	jobCategories: Pick<JobCategory, 'deletedAt' | 'hiddenAt' | 'id' | 'isDefault' | 'legacyCategory'>[] | null | undefined;
	jobCategoryId?: number | null;
	rows: T[] | null | undefined;
}): T | undefined {
	if (!rows?.length) {
		return undefined;
	}

	if (jobCategoryId != null) {
		const pointed = rows.find((row) => row.jobCategoryId === jobCategoryId);

		if (pointed) {
			return pointed;
		}
	}

	const categories = jobCategories ?? [];
	const legacy = effectiveLegacyCategory({
		businessCategory: businessCategory && isBusinessCategoryValue(businessCategory) ? businessCategory : null,
		jobCategory: jobCategoryId != null ? categories.find((category) => category.id === jobCategoryId) : null,
		jobCategoryId: jobCategoryId ?? null,
	});

	// Which enum a row really stands for. A row belonging to a business-defined category
	// stores the inert RESIDENTIAL placeholder in its enum column, so reading that column
	// directly would hand one custom category's settings to another — or to a Residential
	// job. When the row carries a pointer, its category's own legacyCategory decides; only a
	// row written before the pointer existed speaks through the column.
	const rowStandsFor = (row: T, enumValue: BusinessCategory | string) =>
		row.jobCategoryId == null
			? row.businessCategory === enumValue
			: categories.find((category) => category.id === row.jobCategoryId)?.legacyCategory === enumValue;

	if (legacy) {
		const match = rows.find((row) => rowStandsFor(row, legacy));

		if (match) {
			return match;
		}
	}

	const live = categories.filter((category) => category.deletedAt === null && category.hiddenAt === null);
	const fallbackCategory = live.find((category) => category.isDefault) ?? live[0];
	const fallbackLegacy = fallbackCategory?.legacyCategory ?? null;
	const viaDefault = fallbackCategory
		? (rows.find((row) => row.jobCategoryId === fallbackCategory.id) ??
			(fallbackLegacy === null ? undefined : rows.find((row) => rowStandsFor(row, fallbackLegacy))))
		: undefined;

	return viaDefault ?? rows.find((row) => rowStandsFor(row, BusinessCategory.RESIDENTIAL));
}

/**
 * Which category a rate-and-markup lookup should price with, in priority order: the job's,
 * then the quick estimate's, then whatever the caller asked for.
 *
 * A quick estimate has no job but does carry its own category. Callers used to skip that and
 * send PICKUP for every quick estimate, so rates and markups came from a category the user
 * never chose while the deposit — resolved server-side from the real one — came from the
 * category they did.
 */
export function resolveRequestedCategory({
	explicitCategory,
	job,
	quickEstimate,
}: {
	explicitCategory?: BusinessCategory | null;
	job?: { businessCategory: BusinessCategory | null } | null;
	quickEstimate?: { businessCategory: BusinessCategory | null } | null;
}): BusinessCategory | undefined {
	return job?.businessCategory ?? quickEstimate?.businessCategory ?? explicitCategory ?? undefined;
}

/**
 * The category a new job lands in: pickup overrides whatever was requested.
 *
 * Both category columns have to be derived from this one value. Taking the enum from here and
 * the pointer from the requested category would file a pickup job's pricing under the category
 * the user picked instead.
 */
export function resolveCreatedJobCategory({
	isPickup,
	requestedCategory,
}: {
	isPickup: boolean;
	requestedCategory: BusinessCategory | null | undefined;
}): BusinessCategory {
	if (isPickup) {
		return BusinessCategory.PICKUP;
	}

	return requestedCategory ?? BusinessCategory.RESIDENTIAL;
}

/**
 * Picks the JobCategory a legacy enum value refers to, falling back to the business
 * default and then to any live category.
 *
 * Generic over the row shape so callers can select only the columns they need. Callers
 * still pass the enum because the write path has not switched yet, so it is matched to a
 * category by name.
 */
export function resolveJobCategory<T extends JobCategoryIdentity>({
	business,
	category,
	jobCategoryId,
}: {
	business: { jobCategories: T[] | null };
	category: BusinessCategory | null | undefined;
	jobCategoryId?: number | null;
}): T | undefined {
	const categories = business.jobCategories ?? [];
	const wanted = category ?? BusinessCategory.RESIDENTIAL;
	// A deleted category never resolves, whichever way a record names it: callers differ in
	// whether they load deleted rows at all, and a job pointing at one must price the same
	// way everywhere — by the fallbacks below, like a job whose category never existed.
	const present = categories.filter((jobCategory) => jobCategory.deletedAt === null);

	// The pointer wins when the row carries one. It is the only thing that can name a category
	// the business invented, since those have no enum value to match on. Rows written before
	// the pointer existed still have null here and fall through to the enum below.
	const pointedAt = jobCategoryId ? present.find((jobCategory) => jobCategory.id === jobCategoryId) : undefined;

	if (pointedAt) {
		return pointedAt;
	}

	// Matched on legacyCategory, not on the name. The name is what a business renames, and
	// matching on it meant a rename silently fell through to the default category — so an
	// estimate would price with another category's markup and deposit.
	//
	// A job can sit in a category the business has since hidden, so the match considers
	// hidden categories too. Only the fallbacks are restricted to visible ones.
	const exactMatch = present.find((jobCategory) => jobCategory.legacyCategory === wanted);

	if (exactMatch) {
		return exactMatch;
	}

	const live = present.filter((jobCategory) => jobCategory.hiddenAt === null);

	return live.find((jobCategory) => jobCategory.isDefault) ?? live[0] ?? present[0] ?? categories[0];
}

/**
 * Resolves the per-category settings a job prices with.
 *
 * Reads JobCategory rather than BusinessCategorySettings. The legacy table let a category
 * exist with no settings row at all, in which case this synthesized an all-zero row and
 * the job silently priced at 0% markup and 0% deposit. Settings are now columns on the
 * category, so a resolved category always carries real values — the only fallback left is
 * which category to use, never which settings.
 */
export function getBusinessCategorySettingsForCategory({
	business,
	category,
	jobCategoryId,
}: {
	business: { jobCategories: JobCategoryForSettings[] | null };
	category: BusinessCategory | null | undefined;
	jobCategoryId?: number | null;
}): JobCategorySettings {
	return resolveJobCategory({ business, category, jobCategoryId }) ?? buildEmptySettings();
}

type TResolveDefaultDepositPercent = {
	categorySettings: Pick<JobCategorySettings, 'depositPercent'> | null | undefined;
	customer: { depositPercent: Prisma.Decimal | number | null } | null | undefined;
};

/**
 * The *default* deposit percent — what a new estimate starts at, and what the quote form falls back
 * to when an estimate has no `depositPercent` of its own.
 *
 * Returned as a whole-number percent, which is a statement about scale rather than precision: the
 * number is the percent itself (50 = 50%), not a 0-1 fraction like the tax percents on the same
 * settings row. Fractional percents such as 12.5 are legal and are returned unrounded, so callers
 * must not assume an integer.
 *
 * Once an estimate exists, its own `depositPercent` is the source of truth; this only supplies the
 * starting value. A customer-level `depositPercent` overrides the job category's default; null on
 * the customer means "use the category default". Pass `customer: null` where no customer exists
 * (estimate templates) to get the category default.
 *
 * Uses `??` deliberately rather than `||`: 0 is a real override — on-account customers pay no
 * deposit — and truthiness would silently promote it back to the category default.
 */
export function resolveDefaultDepositPercent({ categorySettings, customer }: TResolveDefaultDepositPercent): Prisma.Decimal | number {
	return customer?.depositPercent ?? categorySettings?.depositPercent ?? 0;
}

type TResolveEstimateDepositPercent = TResolveDefaultDepositPercent & {
	// Optional key, not just a nullable value: the client-side `IEstimateForQuote` declares
	// `depositPercent?`, and an absent key means the same thing as null here.
	estimate: { depositPercent?: Prisma.Decimal | number | null } | null | undefined;
};

/**
 * The deposit percent an existing estimate is actually billed at, as a plain number.
 *
 * Full precedence: the estimate's own `depositPercent`, then the customer override, then the job
 * category default, then 0 — the estimate wins because saving one freezes the rate against later
 * settings changes. `resolveDefaultDepositPercent` supplies the tail of that chain and documents the
 * whole-number-percent convention (50 = 50%, fractional values legal and unrounded).
 *
 * `??` throughout, never `||`: 0 is a real value at every level — an estimate deliberately set to 0%
 * must stay at 0% rather than falling back to a nonzero default.
 *
 * Returns `number` rather than `Decimal` because every caller is populating a quote form value;
 * write paths that persist to Prisma want `resolveDefaultDepositPercent` instead.
 */
export function resolveEstimateDepositPercent({ categorySettings, customer, estimate }: TResolveEstimateDepositPercent): number {
	return +(estimate?.depositPercent ?? resolveDefaultDepositPercent({ categorySettings, customer }));
}
